import { type PartialDeep } from 'type-fest';

import { type CanvasElementFrom, type PropsOf } from '@blaze-runtime/engine-domain';

import {
  getSourceProps,
  getSourceTransforms,
} from '../../canvas-elements/element-model/transformations';
import { parkedToggleButtonProps } from '../toggle-button/toggle-button.transforms';
type RadioElement = CanvasElementFrom<'radio'>;
type RadioProps = PartialDeep<PropsOf<'radio'>>;

function transformCheckboxInputToRadio(source: CanvasElementFrom<'checkboxInput'>): RadioElement {
  const props: PartialDeep<PropsOf<'checkboxInput'>> = source.props || {};
  const newProps: RadioProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: undefined, // no mapping
      },
      radio: {
        values: ['Yes', 'No'], // no mapping
        preselect: undefined, // no mapping
        layout: undefined, // no mapping
        readonly: props.content?.input?.readonly,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      radio: {
        label: props.style?.input?.symbol,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformDropdownToRadio(source: CanvasElementFrom<'dropdown'>): RadioElement {
  const props: PartialDeep<PropsOf<'dropdown'>> = source.props || {};
  const newProps: RadioProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: props.content?.data?.defaultValue,
      },
      radio: {
        values: props.content?.menu?.values,
        preselect: props.content?.menu?.preselect,
        layout: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      radio: {
        label: props.style?.input?.color,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformImageToRadio(source: CanvasElementFrom<'image'>): RadioElement {
  const props: PartialDeep<PropsOf<'image'>> = source.props || {};
  const newProps: RadioProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: undefined, // no mapping
      },
      radio: {
        values: undefined, // no mapping
        preselect: undefined, // no mapping
        layout: undefined, // no mapping
        readonly: props.content?.options?.readonly,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: undefined, // no mapping
      },
      radio: {
        label: undefined, // no mapping
        fill: undefined, // no mapping
        border: props.style?.outline?.color,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformTagsToRadio(source: CanvasElementFrom<'tags'>): RadioElement {
  const props: PartialDeep<PropsOf<'tags'>> = source.props || {};
  const newProps: RadioProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: props.content?.data?.defaultValue,
      },
      radio: {
        values: props.content?.tags?.values,
        preselect: props.content?.tags?.preselect?.[0],
        layout: undefined, // no mapping
        readonly: props.content?.options?.readonly,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      radio: {
        label: props.style?.input?.color,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformTextInputToRadio(source: CanvasElementFrom<'textInput'>): RadioElement {
  const props: PartialDeep<PropsOf<'textInput'>> = source.props || {};
  const newProps: RadioProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align?.horizontal,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: undefined, // no mapping
      },
      radio: {
        values: undefined, // no mapping
        preselect: undefined, // no mapping
        layout: undefined, // no mapping
        readonly: props.content?.input?.readonly,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      radio: {
        label: props.style?.input?.value,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformUploadFileToRadio(source: CanvasElementFrom<'uploadFile'>): RadioElement {
  const props: PartialDeep<PropsOf<'uploadFile'>> = source.props || {};
  const newProps: RadioProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: undefined, // no mapping
      },
      radio: {
        values: undefined, // no mapping
        preselect: undefined, // no mapping
        layout: undefined, // no mapping
        readonly: props.content?.button?.readonly,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: undefined, // no mapping
      },
      radio: {
        label: props.style?.uploaded?.label,
        fill: props.style?.uploaded?.fill,
        border: props.style?.uploaded?.outline,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformToggleButtonToRadio(source: CanvasElementFrom<'toggleButton'>): RadioElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'toggleButton', 'radio'>(source, 'radio');
  const newProps: RadioProps = {
    content: {
      title: { ...props.content?.title },
      data: {
        defaultValue: propsT?.content?.data?.defaultValue,
      },
      radio: {
        values: propsT?.content?.radio?.values,
        preselect: propsT?.content?.radio?.preselect,
        layout: propsT?.content?.radio?.layout,
        readonly: propsT?.content?.radio?.readonly,
      },
    },
    style: {
      fontSize: propsT?.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: propsT?.style?.general?.accent,
      },
      radio: {
        label: propsT?.style?.radio?.label,
        fill: propsT?.style?.radio?.fill,
        border: propsT?.style?.radio?.border,
      },
    },
    transforms: {
      toggleButton: parkedToggleButtonProps(props),
    },
  };

  return { props: newProps } as unknown as RadioElement;
}

export const transformToRadio = {
  transformCheckboxInputToRadio,
  transformDropdownToRadio,
  transformImageToRadio,
  transformTagsToRadio,
  transformTextInputToRadio,
  transformToggleButtonToRadio,
  transformUploadFileToRadio,
};
